'use client';

/**
 * AstorPredictionCard Component
 *
 * Dashed mint-border card used to surface a single Astor prediction
 * (predicted next document, suggested next loan, suggested approval, etc.).
 *
 * Layout: AutoAwesome icon (mint) + content column (name + confidence chip +
 * reason) + primary action button on the right.
 *
 * Reusable across portals — name is generic, NOT borrower-specific.
 */

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { iconSizes } from '../../theme/typography';
import { composeSx } from '../../utils/styledUtils';
import { GlassButton } from '../GlassButton';
import { GlassChip } from '../GlassChip';

// ============================================================================
// Types
// ============================================================================

export interface AstorPredictionCardProps {
  /** Predicted thing's name (rendered bold). */
  name: ReactNode;
  /** One-line rationale rendered under the name. */
  reason: ReactNode;
  /** Confidence 0-100. Rounded and clamped before rendering. */
  confidence: number;
  /** Primary action label. */
  actionLabel: ReactNode;
  /** Primary action handler. */
  onAction?: () => void;
  /** Optional icon override (defaults to AutoAwesome). */
  icon?: ReactNode;
  /** Optional confidence chip label override (default `conf {pct}%`). */
  confidenceLabel?: (pct: number) => ReactNode;
  /** Sx override on the outer Box. */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Skeleton
// ============================================================================

function AstorPredictionCardSkeleton() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.75,
        borderRadius: 1.5,
        border: '1px dashed',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Skeleton variant="circular" width={28} height={28} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Skeleton variant="text" width={140} height={20} />
          <Skeleton variant="rounded" width={64} height={20} />
        </Box>
        <Skeleton variant="text" width="80%" height={18} />
      </Box>
      <Skeleton variant="rounded" width={96} height={36} sx={{ flexShrink: 0 }} />
    </Box>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AstorPredictionCard({
  name,
  reason,
  confidence,
  actionLabel,
  onAction,
  icon,
  confidenceLabel = (pct) => `conf ${pct}%`,
  sx,
}: AstorPredictionCardProps) {
  const theme = useTheme();
  const mint = theme.palette.primary.main;
  const pct = Math.round(Math.max(0, Math.min(100, confidence)));

  return (
    <Box
      sx={composeSx(
        {
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          p: 1.75,
          borderRadius: 1.5,
          border: `1px dashed ${alpha(mint, 0.5)}`,
          backgroundColor: alpha(mint, 0.05),
        },
        sx,
      )}
    >
      {/* Icon */}
      <Box
        aria-hidden
        sx={{
          flexShrink: 0,
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: alpha(mint, 0.16),
          color: mint,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon ?? <AutoAwesomeIcon sx={{ fontSize: iconSizes.md }} />}
      </Box>

      {/* Content column */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              lineHeight: 1.35,
            }}
          >
            {name}
          </Typography>
          <GlassChip
            label={confidenceLabel(pct)}
            size="small"
            color="success"
            sx={{
              height: 20,
              fontWeight: 600,
              borderRadius: 1,
              fontFeatureSettings: '"tnum"',
            }}
          />
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.5,
          }}
        >
          {reason}
        </Typography>
      </Box>

      {/* Action */}
      {onAction && (
        <GlassButton
          variant="outlined"
          color="primary"
          size="small"
          onClick={onAction}
          sx={{ flexShrink: 0, minHeight: 44 }}
        >
          {actionLabel}
        </GlassButton>
      )}
    </Box>
  );
}

AstorPredictionCard.displayName = 'AstorPredictionCard';
AstorPredictionCard.Skeleton = AstorPredictionCardSkeleton;

export { AstorPredictionCardSkeleton };
export default AstorPredictionCard;
